'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ProbabilityCalculator() {
  const t = useTranslations("tools.probabilityCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState('twoEvents');

  // Two Events Calculator State
  const [twoEventsInputs, setTwoEventsInputs] = useState({
    pa: 0.5,
    pb: 0.4
  });
  const [twoEventsResults, setTwoEventsResults] = useState<any>(null);

  // Solver State
  const [solverInputs, setSolverInputs] = useState({
    pa: '',
    pb: '',
    na: '',
    nb: '',
    and: '',
    or: '',
    xor: '',
    neither: ''
  });
  const [solverResults, setSolverResults] = useState<any>(null);

  // Series State
  const [seriesInputs, setSeriesInputs] = useState({
    pa: 0.6,
    ra: 5,
    pb: 0.3,
    rb: 3
  });
  const [seriesResults, setSeriesResults] = useState<any>(null);

  // Normal Distribution State
  const [normalInputs, setNormalInputs] = useState({
    mean: 0,
    stddev: 1,
    leftBound: '-1',
    rightBound: '1'
  });
  const [normalResults, setNormalResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1e40af',
    secondary: '#7c3aed',
    accent: '#06b6d4',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    success: '#15803d',
    successBg: '#f0fdf4',
    successBorder: '#86efac',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '1000px', margin: '0 auto' },
      {},
      {},
      {}
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2rem' }
    ),
    toolTitle: getResponsiveStyle(
      { color: colors.primary, fontWeight: 700, marginBottom: '0.5rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    tabsContainer: getResponsiveStyle(
      {
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        justifyContent: 'center',
        flexWrap: 'wrap' as const
      },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      { flexDirection: 'row' as const, marginBottom: '2rem' }
    ),
    tabButton: (isActive: boolean) => getResponsiveStyle(
      {
        padding: '0.75rem 1.5rem',
        background: isActive ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
        color: isActive ? 'white' : colors.textSecondary,
        border: isActive ? 'none' : '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        fontWeight: 600,
        transition: 'all 0.3s ease',
        fontSize: '0.95rem'
      },
      { width: '100%' },
      { width: 'auto' },
      { width: 'auto' }
    ),
    calculatorSection: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    sectionTitle: getResponsiveStyle(
      {
        color: colors.primary,
        fontWeight: 600,
        marginBottom: '1rem',
        paddingBottom: '0.5rem',
        borderBottom: '2px solid #e5e7eb'
      },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    sectionDescription: getResponsiveStyle(
      {
        color: colors.textSecondary,
        marginBottom: '1.5rem',
        lineHeight: 1.6
      },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    form: {
      display: 'grid',
      gap: '1.5rem'
    },
    formRow: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1rem'
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const
    },
    label: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '0.5rem'
      },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        outline: 'none'
      },
      {},
      {},
      {}
    ),
    hint: getResponsiveStyle(
      {
        color: '#9ca3af',
        marginTop: '0.25rem'
      },
      { fontSize: '0.8rem' },
      { fontSize: '0.85rem' },
      { fontSize: '0.85rem' }
    ),
    calculateButton: getResponsiveStyle(
      {
        padding: '1rem 2rem',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginTop: '1rem',
        width: '100%'
      },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    resultsCard: getResponsiveStyle(
      {
        marginTop: '2rem',
        padding: '1.5rem',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: '1rem',
        border: '2px solid #86efac'
      },
      {},
      {},
      {}
    ),
    resultsTitle: getResponsiveStyle(
      {
        color: colors.success,
        marginBottom: '1rem',
        fontWeight: 600
      },
      { fontSize: '1.2rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.3rem' }
    ),
    resultGrid: {
      display: 'grid',
      gap: '1rem'
    },
    resultItem: getResponsiveStyle(
      {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem',
        background: 'white',
        borderRadius: '0.5rem',
        borderLeft: '4px solid #2563eb'
      },
      { flexDirection: 'column' as const, alignItems: 'flex-start', gap: '0.5rem' },
      { flexDirection: 'row' as const, alignItems: 'center', gap: '0' },
      { flexDirection: 'row' as const, alignItems: 'center', gap: '0' }
    ),
    resultLabel: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary
      },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    resultValue: getResponsiveStyle(
      {
        fontWeight: 700,
        color: colors.primary
      },
      { fontSize: '1.1rem' },
      { fontSize: '1.15rem' },
      { fontSize: '1.2rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    h2: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '1rem'
      },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '0.75rem',
        marginTop: '1.5rem'
      },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      {
        lineHeight: 1.7,
        color: colors.textSecondary,
        marginBottom: '1rem'
      },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      {
        marginLeft: '1.5rem',
        marginBottom: '1rem',
        lineHeight: 1.7,
        color: colors.textSecondary
      },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.5rem'
    },
    faqItem: getResponsiveStyle(
      {
        marginBottom: '1.5rem',
        padding: '1.5rem',
        background: 'rgba(255, 255, 255, 0.5)',
        borderRadius: '0.75rem',
        border: '1px solid rgba(37, 99, 235, 0.1)'
      },
      {},
      {},
      {}
    ),
    faqQuestion: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '0.75rem'
      },
      { fontSize: '1.1rem' },
      { fontSize: '1.15rem' },
      { fontSize: '1.2rem' }
    ),
    faqAnswer: getResponsiveStyle(
      {
        color: colors.textSecondary,
        lineHeight: 1.6
      },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    refLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
      transition: 'color 0.2s ease'
    }
  };

  // Two Events Calculator
  const handleTwoEventsCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { pa, pb } = twoEventsInputs;

    if (pa < 0 || pa > 1 || pb < 0 || pb > 1) {
      alert(t('errorInvalidProbability'));
      return;
    }

    const notA = 1 - pa;
    const notB = 1 - pb;
    const intersection = pa * pb;
    const union = pa + pb - intersection;
    const xor = pa + pb - 2 * intersection;
    const neither = 1 - union;

    setTwoEventsResults({
      notA,
      notB,
      intersection,
      union,
      xor,
      neither
    });
  };

  // Solver Calculator
  const handleSolverCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const values: any = {};
    let count = 0;

    Object.entries(solverInputs).forEach(([key, value]) => {
      if (value !== '') {
        values[key] = parseFloat(value);
        count++;
      }
    });

    if (count < 2) {
      alert(t('errorMinimumValues'));
      return;
    }

    let pa, pb;

    if (values.pa !== undefined) pa = values.pa;
    else if (values.na !== undefined) pa = 1 - values.na;

    if (values.pb !== undefined) pb = values.pb;
    else if (values.nb !== undefined) pb = 1 - values.nb;

    if (pa !== undefined && values.and !== undefined && pb === undefined) {
      pb = values.and / pa;
    }

    if (pb !== undefined && values.and !== undefined && pa === undefined) {
      pa = values.and / pb;
    }

    if (pa !== undefined && pb !== undefined) {
      const notA = 1 - pa;
      const notB = 1 - pb;
      const intersection = pa * pb;
      const union = pa + pb - intersection;
      const xor = pa + pb - 2 * intersection;
      const neither = 1 - union;

      setSolverResults({
        pa,
        pb,
        notA,
        notB,
        intersection,
        union,
        xor,
        neither
      });
    } else {
      alert(t('errorUnableToSolve'));
    }
  };

  // Series Calculator
  const handleSeriesCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { pa, ra, pb, rb } = seriesInputs;

    if (pa < 0 || pa > 1 || pb < 0 || pb > 1) {
      alert(t('errorInvalidProbability'));
      return;
    }

    const probA = Math.pow(pa, ra);
    const probB = Math.pow(pb, rb);
    const combined = probA * probB;

    setSeriesResults({
      probA,
      probB,
      combined,
      ra,
      rb
    });
  };

  // Normal Distribution Calculator
  const erf = (x: number): number => {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x);

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  };

  const normalCDF = (x: number, mean: number, stddev: number): number => {
    return 0.5 * (1 + erf((x - mean) / (stddev * Math.sqrt(2))));
  };

  const handleNormalCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { mean, stddev, leftBound, rightBound } = normalInputs;

    if (stddev <= 0) {
      alert(t('errorInvalidStdDev'));
      return;
    }

    let leftProb, rightProb;
    const leftBoundNum = leftBound;
    const rightBoundNum = rightBound;

    if (leftBound === '-inf' || leftBound === '-Infinity') {
      leftProb = 0;
    } else {
      leftProb = normalCDF(parseFloat(leftBound), mean, stddev);
    }

    if (rightBound === 'inf' || rightBound === 'Infinity') {
      rightProb = 1;
    } else {
      rightProb = normalCDF(parseFloat(rightBound), mean, stddev);
    }

    const probability = rightProb - leftProb;

    const confidenceLevels = [0.90, 0.95, 0.99];
    const zScores = [1.645, 1.96, 2.576];

    const intervals = confidenceLevels.map((level, i) => {
      const margin = zScores[i] * stddev;
      return {
        level,
        lower: mean - margin,
        upper: mean + margin
      };
    });

    setNormalResults({
      probability,
      leftBound: leftBoundNum,
      rightBound: rightBoundNum,
      intervals
    });
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolTitle}>{t('toolTitle')}</h2>
          </div>

          <div style={styles.tabsContainer}>
            <button
              style={styles.tabButton(activeTab === 'twoEvents')}
              onClick={() => setActiveTab('twoEvents')}
              onMouseEnter={(e) => {
                if (activeTab !== 'twoEvents') {
                  e.currentTarget.style.borderColor = colors.primary;
                  e.currentTarget.style.color = colors.primary;
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'twoEvents') {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = colors.textSecondary;
                }
              }}
            >
              {t('tabTwoEvents')}
            </button>
            <button
              style={styles.tabButton(activeTab === 'solver')}
              onClick={() => setActiveTab('solver')}
              onMouseEnter={(e) => {
                if (activeTab !== 'solver') {
                  e.currentTarget.style.borderColor = colors.primary;
                  e.currentTarget.style.color = colors.primary;
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'solver') {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = colors.textSecondary;
                }
              }}
            >
              {t('tabSolver')}
            </button>
            <button
              style={styles.tabButton(activeTab === 'series')}
              onClick={() => setActiveTab('series')}
              onMouseEnter={(e) => {
                if (activeTab !== 'series') {
                  e.currentTarget.style.borderColor = colors.primary;
                  e.currentTarget.style.color = colors.primary;
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'series') {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = colors.textSecondary;
                }
              }}
            >
              {t('tabSeries')}
            </button>
            <button
              style={styles.tabButton(activeTab === 'normal')}
              onClick={() => setActiveTab('normal')}
              onMouseEnter={(e) => {
                if (activeTab !== 'normal') {
                  e.currentTarget.style.borderColor = colors.primary;
                  e.currentTarget.style.color = colors.primary;
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'normal') {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = colors.textSecondary;
                }
              }}
            >
              {t('tabNormal')}
            </button>
          </div>

          {/* Two Events Calculator */}
          {activeTab === 'twoEvents' && (
            <div style={styles.calculatorSection}>
              <h3 style={styles.sectionTitle}>{t('twoEventsTitle')}</h3>
              <p style={styles.sectionDescription}>{t('twoEventsDescription')}</p>

              <form style={styles.form} onSubmit={handleTwoEventsCalculate}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelPA')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={twoEventsInputs.pa}
                      onChange={(e) => setTwoEventsInputs({...twoEventsInputs, pa: parseFloat(e.target.value)})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      required
                    />
                    <span style={styles.hint}>{t('hintProbability')}</span>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelPB')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={twoEventsInputs.pb}
                      onChange={(e) => setTwoEventsInputs({...twoEventsInputs, pb: parseFloat(e.target.value)})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      required
                    />
                    <span style={styles.hint}>{t('hintProbability')}</span>
                  </div>
                </div>
                <button
                  type="submit"
                  style={styles.calculateButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t('calculateButton')}
                </button>
              </form>

              {twoEventsResults && (
                <div style={styles.resultsCard}>
                  <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>
                  <div style={styles.resultGrid}>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultNotA')}</span>
                      <span style={styles.resultValue}>{twoEventsResults.notA.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultNotB')}</span>
                      <span style={styles.resultValue}>{twoEventsResults.notB.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultIntersection')}</span>
                      <span style={styles.resultValue}>{twoEventsResults.intersection.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultUnion')}</span>
                      <span style={styles.resultValue}>{twoEventsResults.union.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultXor')}</span>
                      <span style={styles.resultValue}>{twoEventsResults.xor.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultNeither')}</span>
                      <span style={styles.resultValue}>{twoEventsResults.neither.toFixed(4)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Solver Calculator */}
          {activeTab === 'solver' && (
            <div style={styles.calculatorSection}>
              <h3 style={styles.sectionTitle}>{t('solverTitle')}</h3>
              <p style={styles.sectionDescription}>{t('solverDescription')}</p>

              <form style={styles.form} onSubmit={handleSolverCalculate}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelPA')}</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      max="1"
                      value={solverInputs.pa}
                      onChange={(e) => setSolverInputs({...solverInputs, pa: e.target.value})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelPB')}</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      max="1"
                      value={solverInputs.pb}
                      onChange={(e) => setSolverInputs({...solverInputs, pb: e.target.value})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelNotA')}</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      max="1"
                      value={solverInputs.na}
                      onChange={(e) => setSolverInputs({...solverInputs, na: e.target.value})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelNotB')}</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      max="1"
                      value={solverInputs.nb}
                      onChange={(e) => setSolverInputs({...solverInputs, nb: e.target.value})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelAnd')}</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      max="1"
                      value={solverInputs.and}
                      onChange={(e) => setSolverInputs({...solverInputs, and: e.target.value})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelOr')}</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      max="1"
                      value={solverInputs.or}
                      onChange={(e) => setSolverInputs({...solverInputs, or: e.target.value})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelXor')}</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      max="1"
                      value={solverInputs.xor}
                      onChange={(e) => setSolverInputs({...solverInputs, xor: e.target.value})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelNeither')}</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      max="1"
                      value={solverInputs.neither}
                      onChange={(e) => setSolverInputs({...solverInputs, neither: e.target.value})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  style={styles.calculateButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t('solveButton')}
                </button>
              </form>

              {solverResults && (
                <div style={styles.resultsCard}>
                  <h3 style={styles.resultsTitle}>{t('solvedValuesTitle')}</h3>
                  <div style={styles.resultGrid}>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('labelPA')}</span>
                      <span style={styles.resultValue}>{solverResults.pa.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('labelPB')}</span>
                      <span style={styles.resultValue}>{solverResults.pb.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultNotA')}</span>
                      <span style={styles.resultValue}>{solverResults.notA.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultNotB')}</span>
                      <span style={styles.resultValue}>{solverResults.notB.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultIntersection')}</span>
                      <span style={styles.resultValue}>{solverResults.intersection.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultUnion')}</span>
                      <span style={styles.resultValue}>{solverResults.union.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultXor')}</span>
                      <span style={styles.resultValue}>{solverResults.xor.toFixed(4)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultNeither')}</span>
                      <span style={styles.resultValue}>{solverResults.neither.toFixed(4)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Series Calculator */}
          {activeTab === 'series' && (
            <div style={styles.calculatorSection}>
              <h3 style={styles.sectionTitle}>{t('seriesTitle')}</h3>
              <p style={styles.sectionDescription}>{t('seriesDescription')}</p>

              <form style={styles.form} onSubmit={handleSeriesCalculate}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelEventAProbability')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={seriesInputs.pa}
                      onChange={(e) => setSeriesInputs({...seriesInputs, pa: parseFloat(e.target.value)})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelEventARepeat')}</label>
                    <input
                      type="number"
                      min="1"
                      value={seriesInputs.ra}
                      onChange={(e) => setSeriesInputs({...seriesInputs, ra: parseInt(e.target.value)})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      required
                    />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelEventBProbability')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={seriesInputs.pb}
                      onChange={(e) => setSeriesInputs({...seriesInputs, pb: parseFloat(e.target.value)})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelEventBRepeat')}</label>
                    <input
                      type="number"
                      min="1"
                      value={seriesInputs.rb}
                      onChange={(e) => setSeriesInputs({...seriesInputs, rb: parseInt(e.target.value)})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  style={styles.calculateButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t('calculateSeriesButton')}
                </button>
              </form>

              {seriesResults && (
                <div style={styles.resultsCard}>
                  <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>
                  <div style={styles.resultGrid}>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultEventAOccurring', {times: seriesResults.ra})}</span>
                      <span style={styles.resultValue}>{seriesResults.probA.toFixed(6)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultEventBOccurring', {times: seriesResults.rb})}</span>
                      <span style={styles.resultValue}>{seriesResults.probB.toFixed(6)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultCombinedProbability', {timesA: seriesResults.ra, timesB: seriesResults.rb})}</span>
                      <span style={styles.resultValue}>{seriesResults.combined.toFixed(6)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultPercentage')}</span>
                      <span style={styles.resultValue}>{(seriesResults.combined * 100).toFixed(4)}%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Normal Distribution Calculator */}
          {activeTab === 'normal' && (
            <div style={styles.calculatorSection}>
              <h3 style={styles.sectionTitle}>{t('normalTitle')}</h3>
              <p style={styles.sectionDescription}>{t('normalDescription')}</p>

              <form style={styles.form} onSubmit={handleNormalCalculate}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelMean')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={normalInputs.mean}
                      onChange={(e) => setNormalInputs({...normalInputs, mean: parseFloat(e.target.value)})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelStdDev')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={normalInputs.stddev}
                      onChange={(e) => setNormalInputs({...normalInputs, stddev: parseFloat(e.target.value)})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      required
                    />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelLeftBound')}</label>
                    <input
                      type="text"
                      value={normalInputs.leftBound}
                      onChange={(e) => setNormalInputs({...normalInputs, leftBound: e.target.value})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      required
                    />
                    <span style={styles.hint}>{t('hintInfinity')}</span>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('labelRightBound')}</label>
                    <input
                      type="text"
                      value={normalInputs.rightBound}
                      onChange={(e) => setNormalInputs({...normalInputs, rightBound: e.target.value})}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      required
                    />
                    <span style={styles.hint}>{t('hintInfinity')}</span>
                  </div>
                </div>
                <button
                  type="submit"
                  style={styles.calculateButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t('calculateButton')}
                </button>
              </form>

              {normalResults && (
                <div style={styles.resultsCard}>
                  <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>
                  <div style={styles.resultGrid}>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultProbabilityRange', {left: normalResults.leftBound, right: normalResults.rightBound})}</span>
                      <span style={styles.resultValue}>{normalResults.probability.toFixed(6)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultPercentage')}</span>
                      <span style={styles.resultValue}>{(normalResults.probability * 100).toFixed(4)}%</span>
                    </div>
                    <div style={{...styles.resultItem, gridColumn: '1/-1', background: '#f0f9ff', borderLeft: '4px solid #7c3aed'}}>
                      <span style={{...styles.resultLabel, fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem'}}>{t('confidenceIntervalsTitle')}</span>
                    </div>
                    {normalResults.intervals.map((interval: any, index: number) => (
                      <div key={index} style={styles.resultItem}>
                        <span style={styles.resultLabel}>{t('confidenceInterval', {level: (interval.level * 100)})}</span>
                        <span style={styles.resultValue}>[{interval.lower.toFixed(4)}, {interval.upper.toFixed(4)}]</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToUseStep1')}</li>
          <li style={styles.listItem}>{t('howToUseStep2')}</li>
          <li style={styles.listItem}>{t('howToUseStep3')}</li>
          <li style={styles.listItem}>{t('howToUseStep4')}</li>
        </ol>
      </section>

      {/* Key Features */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('featuresTitle')}</h2>
        <h3 style={styles.h3}>{t('featureSpeedTitle')}</h3>
        <p style={styles.paragraph}>{t('featureSpeedDescription')}</p>

        <h3 style={styles.h3}>{t('featureAccuracyTitle')}</h3>
        <p style={styles.paragraph}>{t('featureAccuracyDescription')}</p>

        <h3 style={styles.h3}>{t('featureAccessibilityTitle')}</h3>
        <p style={styles.paragraph}>{t('featureAccessibilityDescription')}</p>

        <h3 style={styles.h3}>{t('featureVersatilityTitle')}</h3>
        <p style={styles.paragraph}>{t('featureVersatilityDescription')}</p>
      </section>

      {/* Applications */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('applicationsTitle')}</h2>

        <h3 style={styles.h3}>{t('appEducationTitle')}</h3>
        <p style={styles.paragraph}>{t('appEducationDescription')}</p>

        <h3 style={styles.h3}>{t('appBusinessTitle')}</h3>
        <p style={styles.paragraph}>{t('appBusinessDescription')}</p>

        <h3 style={styles.h3}>{t('appHealthcareTitle')}</h3>
        <p style={styles.paragraph}>{t('appHealthcareDescription')}</p>

        <h3 style={styles.h3}>{t('appEngineeringTitle')}</h3>
        <p style={styles.paragraph}>{t('appEngineeringDescription')}</p>

        <h3 style={styles.h3}>{t('appFinanceTitle')}</h3>
        <p style={styles.paragraph}>{t('appFinanceDescription')}</p>
      </section>

      {/* Best Practices */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('bestPracticesTitle')}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('bestPractice1')}</li>
          <li style={styles.listItem}>{t('bestPractice2')}</li>
          <li style={styles.listItem}>{t('bestPractice3')}</li>
          <li style={styles.listItem}>{t('bestPractice4')}</li>
          <li style={styles.listItem}>{t('bestPractice5')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>
            <a
              href="https://probabilitycalculator.online"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Probability Calculator – Probability Calculator
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.omnicalculator.com/statistics/probability"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Probability Calculator - Omni Calculator
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.toolshero.com/featured-posts/different-applications-of-probability-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              What Are The Different Applications of Probability Calculator?
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.fidelity.com/learning-center/tools-demos/research-tools/probability-calculator-video"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Using the probability calculator - Fidelity Investments
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.bettoredge.com/implied-probability-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Implied Probability Calculator for Sports Betting - Bettor Edge
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://digitaltools.me/probability-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Probability Calculator - Digital Tools
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}